/**
 * GuideViewer — Renders a user guide as a professional HTML document.
 * Supports print/PDF export, table of contents, and Eyvo branding.
 */
import React, { useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Printer, Download, ChevronLeft, BookOpen } from 'lucide-react';
import eyvoLogo from '../../assets/eyvo_report_logo.png';

export default function GuideViewer({ title, subtitle, children, onBack }) {
  const contentRef = useRef(null);

  const handlePrint = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const pw = window.open('', '_blank', 'width=900,height=700');
    pw.document.write(`<!DOCTYPE html><html><head>
      <title>${title} — Eyvo eProcurement</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, sans-serif; color: #1e293b; line-height: 1.7; padding: 40px 50px; font-size: 11pt; }
        .guide-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #1e3a5f; padding-bottom: 16px; margin-bottom: 30px; }
        .guide-header h1 { font-family: 'Space Grotesk', sans-serif; font-size: 22pt; color: #1e3a5f; }
        .guide-header .subtitle { font-size: 10pt; color: #64748b; margin-top: 4px; }
        .guide-header img { height: 36px; }
        h2 { font-family: 'Space Grotesk', sans-serif; font-size: 16pt; color: #1e3a5f; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin: 28px 0 14px; page-break-after: avoid; }
        h3 { font-size: 13pt; color: #334155; margin: 20px 0 10px; font-weight: 600; page-break-after: avoid; }
        h4 { font-size: 11pt; color: #475569; margin: 14px 0 8px; font-weight: 600; }
        p { margin: 0 0 10px; }
        ul, ol { margin: 0 0 12px 24px; }
        li { margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0 18px; font-size: 10pt; }
        th { background: #f1f5f9; border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; font-weight: 600; color: #334155; }
        td { border: 1px solid #e2e8f0; padding: 6px 10px; vertical-align: top; }
        .callout { background: #eff6ff; border-left: 4px solid #3b82f6; padding: 12px 16px; margin: 14px 0; border-radius: 0 6px 6px 0; font-size: 10pt; }
        .callout-warn { background: #fefce8; border-left-color: #eab308; }
        .callout-tip { background: #f0fdf4; border-left-color: #22c55e; }
        .callout strong { display: block; margin-bottom: 4px; }
        .screenshot { max-width: 100%; border: 1px solid #e2e8f0; border-radius: 6px; margin: 10px 0 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
        .field-table td:first-child { font-weight: 600; white-space: nowrap; width: 200px; color: #1e3a5f; }
        code { background: #f1f5f9; padding: 2px 6px; border-radius: 3px; font-size: 9.5pt; color: #7c3aed; }
        .toc { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px 28px; margin: 0 0 28px; }
        .toc h3 { margin: 0 0 12px; font-size: 12pt; color: #1e3a5f; }
        .toc ol { counter-reset: toc-item; list-style: none; margin: 0; padding: 0; }
        .toc li { counter-increment: toc-item; padding: 3px 0; }
        .toc li::before { content: counter(toc-item) ". "; font-weight: 600; color: #3b82f6; }
        .toc a { color: #1e3a5f; text-decoration: none; }
        .toc a:hover { text-decoration: underline; }
        .footer { margin-top: 40px; padding-top: 16px; border-top: 2px solid #1e3a5f; font-size: 9pt; color: #94a3b8; display: flex; justify-content: space-between; }
        @media print {
          body { padding: 20px 30px; }
          .no-print { display: none !important; }
          h2 { page-break-after: avoid; }
          .screenshot { max-width: 95%; }
        }
      </style>
    </head><body>
      <div class="guide-header">
        <div><h1>${title}</h1><div class="subtitle">${subtitle || 'Eyvo eProcurement User Guide'}</div></div>
        <img src="${window.location.origin}${eyvoLogo}" alt="Eyvo" />
      </div>
      ${content.innerHTML}
      <div class="footer">
        <span>&copy; ${new Date().getFullYear()} Eyvo Inc. All rights reserved.</span>
        <span>www.eyvo.com</span>
      </div>
    </body></html>`);
    pw.document.close();
    setTimeout(() => pw.print(), 600);
  }, [title, subtitle]);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 no-print" data-testid="guide-toolbar">
        {onBack && (
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="guide-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Button size="sm" variant="outline" onClick={handlePrint} data-testid="guide-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between border-b-[3px] pb-4 mb-8" style={{ borderColor: '#1e3a5f' }}>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1e3a5f' }}>{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <img src={eyvoLogo} alt="Eyvo" className="h-9" />
      </div>

      {/* Content */}
      <div ref={contentRef} className="guide-content prose prose-slate max-w-none" data-testid="guide-content">
        {children}
      </div>

      {/* Footer */}
      <div className="mt-10 pt-4 border-t-2 flex items-center justify-between text-xs text-muted-foreground" style={{ borderColor: '#1e3a5f' }}>
        <span>&copy; {new Date().getFullYear()} Eyvo Inc. All rights reserved.</span>
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.eyvo.com</a>
      </div>
    </div>
  );
}

/** Reusable guide content components */
export function GuideSection({ id, title, children }) {
  return (
    <section id={id} className="mb-8">
      <h2 className="text-lg font-bold border-b pb-1.5 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1e3a5f' }}>{title}</h2>
      {children}
    </section>
  );
}

export function GuideSubSection({ title, children }) {
  return (
    <div className="mb-5">
      <h3 className="text-base font-semibold text-slate-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export function GuideCallout({ type = 'info', title, children }) {
  const styles = {
    info: 'bg-blue-50 border-l-4 border-blue-500',
    warn: 'bg-amber-50 border-l-4 border-amber-500',
    tip: 'bg-emerald-50 border-l-4 border-emerald-500',
  };
  return (
    <div className={`${styles[type]} rounded-r-md px-4 py-3 my-3 text-sm`}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      {children}
    </div>
  );
}

export function GuideFieldTable({ fields }) {
  return (
    <table className="w-full text-sm border-collapse my-3">
      <thead>
        <tr className="bg-slate-50">
          <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700 w-[200px]">Field / Element</th>
          <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">Description</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((f, i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-50/50' : ''}>
            <td className="border border-slate-200 px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{f.name}</td>
            <td className="border border-slate-200 px-3 py-2 text-slate-600">{f.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function GuideTOC({ items }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-6 py-5 mb-8">
      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> Table of Contents</h3>
      <ol className="list-decimal list-inside space-y-1 text-sm">
        {items.map((item, i) => (
          <li key={i}><a href={`#${item.id}`} className="text-primary hover:underline">{item.label}</a></li>
        ))}
      </ol>
    </div>
  );
}
